import {
  countryCodes,
  getCountryName,
  getCountryCallingCode,
} from '../../../../../ui_system/inputs/countryUtils';

// Types
export interface CountryOption {
  code: string;
  dialCode: string;
  flag: string;
  name: string;
}

export interface ValidationState {
  [field: string]: string[];
}

// Convert country code to flag emoji
const getCountryFlag = (countryCode: string): string => {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

// Generate full country list from countryUtils
export const COUNTRIES: CountryOption[] = countryCodes.map((code: string) => ({
  code,
  dialCode: `+${getCountryCallingCode(code)}`,
  flag: getCountryFlag(code),
  name: getCountryName(code) || code,
}));

// Get placeholder number based on country
export const getPlaceholder = (
  countryCode: string,
  dialCode: string
): string => {
  switch (countryCode) {
    case 'US':
    case 'CA':
      return `${dialCode} (555) 123-4567`;
    case 'GB':
      return `${dialCode} 7911 123456`;
    case 'AU':
      return `${dialCode} 412 345 678`;
    case 'DE':
      return `${dialCode} 30 12345678`;
    case 'FR':
      return `${dialCode} 6 12 34 56 78`;
    default:
      return `${dialCode} 123456789`;
  }
};

// Format phone number input based on various patterns
export const formatPhoneNumber = (
  value: string,
  selectedCountryDialCode: string
): string => {
  // Handle autofill or manual input
  if (value && !value.startsWith('+')) {
    // This might be autofill or manual input without country code
    // Try to detect if it's a US number (starts with 1 or area code)
    if (value.match(/^1\d{10}$/)) {
      // Full US number with country code
      return `+1 ${value.slice(1)}`;
    }
    if (value.match(/^\d{10}$/)) {
      // US number without country code
      return `+1 ${value}`;
    }
    if (value.match(/^\d{3}-\d{3}-\d{4}$/)) {
      // US number with dashes (415-629-8669)
      const digits = value.replace(/-/g, '');
      return `+1 ${digits}`;
    }
    if (value.match(/^\(\d{3}\)\s?\d{3}-\d{4}$/)) {
      // US number with parentheses and dashes
      const digits = value.replace(/[^\d]/g, '');
      return `+1 ${digits}`;
    }
    // For other formats, try to clean and add current country code
    const cleaned = value.replace(/[^\d]/g, '');
    if (cleaned) {
      return `${selectedCountryDialCode} ${cleaned}`;
    }
  } else {
    // User is typing with country code, clean up the input
    value = value.replace(/[^\d+\s()-]/g, '');

    // If user completely clears the input, start with dial code
    if (value === '' || value === '+') {
      return `${selectedCountryDialCode} `;
    }
    // If user deletes part of dial code, restore it
    if (!value.startsWith(selectedCountryDialCode)) {
      // Extract just the number part (everything after any existing dial code)
      const numberPart = value.replace(/^\+?\d{1,4}\s?/, '');
      return `${selectedCountryDialCode} ${numberPart}`;
    }
  }

  return value;
};

// Extract number part from phone number without dial code
export const extractNumberPart = (
  phoneNumber: string,
  currentDialCode: string
): string => {
  if (phoneNumber.startsWith(currentDialCode)) {
    return phoneNumber.slice(currentDialCode.length).trim();
  }
  // Remove any existing dial code
  return phoneNumber.replace(/^\+?\d{1,4}\s?/, '').trim();
};

// PersonalModern validation helpers
export const validateField = (
  fieldName: string,
  value: string,
  phoneMandatory: boolean,
  showZipCodeOnLive: boolean,
  t: (key: string) => string
): string[] => {
  const fieldErrors: string[] = [];

  switch (fieldName) {
    case 'first_name':
    case 'last_name':
      if (!value.trim()) {
        fieldErrors.push(t('CANT_BE_BLANK'));
      }
      break;
    case 'email':
      if (!value.trim()) {
        fieldErrors.push(t('CANT_BE_BLANK'));
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        fieldErrors.push(t('EMAIL_IS_INVALID'));
      }
      break;
    case 'phone':
      if (phoneMandatory && !value.trim()) {
        fieldErrors.push(t('CANT_BE_BLANK'));
      }
      break;
    case 'zip_code':
      if (showZipCodeOnLive && !value.trim()) {
        fieldErrors.push(t('CANT_BE_BLANK'));
      }
      break;
    default:
      break;
  }

  return fieldErrors;
};

export const getFieldErrors = (
  _fieldName: string, // eslint-disable-line @typescript-eslint/no-unused-vars
  serverErrors: string[],
  localErrors: string[],
  showValidationErrors: boolean
): string[] => {
  if (serverErrors.length > 0) {
    return serverErrors;
  }
  if (showValidationErrors) {
    return localErrors;
  }
  return [];
};

export const hasFieldError = (fieldErrors: string[]): boolean => {
  return fieldErrors.length > 0;
};

export const scrollToFirstError = (
  getErrorsForField: (fieldName: string) => string[],
  containerRef: React.RefObject<HTMLDivElement>
): void => {
  const errorFields = ['first_name', 'last_name', 'email', 'phone'];

  for (const fieldName of errorFields) {
    const fieldErrors = getErrorsForField(fieldName);
    if (fieldErrors.length > 0) {
      const element = containerRef.current?.querySelector(
        `[data-field="${fieldName}"]`
      ) as HTMLElement;
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        element.focus();
        break;
      }
    }
  }
};

// Field visibility helpers
export const shouldShowNameFields = (fieldsToHide: string[]): boolean => {
  return !['name'].every((field) => fieldsToHide.includes(field));
};

export const shouldShowEmailField = (fieldsToHide: string[]): boolean => {
  return !fieldsToHide.includes('email');
};
